import React, { useState } from 'react';
import { Truck, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './ui/LoadingSpinner';

export default function LoginPage({ onSwitchToSignUp, onForgotPassword }) {
  const { loginWithEmail, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    setSigningIn(true);
    try {
      await loginWithEmail(email, password);
    } catch (err) {
      setLocalError(err.message || 'Failed to sign in. Please try again.');
      setSigningIn(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen bg-locate2-bg-light flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-10 h-10 bg-locate2-primary rounded-lg flex items-center justify-center">
              <Truck size={24} className="text-white" />
            </div>
            <span className="text-2xl font-bold text-locate2-dark-blue">Locate2u</span>
          </div>
          <h1 className="text-2xl font-bold text-locate2-dark-blue mb-2">
            Import Processing Portal
          </h1>
          <p className="text-locate2-light-gray">Sign in to manage your imports</p>
        </div>

        {displayError && (
          <div className="mb-4 p-3 bg-status-error-bg text-status-error rounded-lg text-sm">
            {displayError}
          </div>
        )}

        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-locate2-medium-gray mb-1">
              Email
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-locate2-light-gray" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-locate2-border rounded-lg focus:border-locate2-primary focus:outline-none transition-colors"
                placeholder="Enter your email"
                disabled={signingIn}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-locate2-medium-gray mb-1">
              Password
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-locate2-light-gray" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border-2 border-locate2-border rounded-lg focus:border-locate2-primary focus:outline-none transition-colors"
                placeholder="Enter your password"
                disabled={signingIn}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-locate2-light-gray hover:text-locate2-medium-gray"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-sm text-locate2-primary hover:text-locate2-dark-blue transition-colors"
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={signingIn}
            className="w-full flex items-center justify-center gap-2 bg-locate2-primary text-white rounded-lg px-4 py-3 font-medium hover:bg-locate2-dark-blue transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {signingIn ? <LoadingSpinner size="sm" className="border-white border-t-transparent" /> : null}
            {signingIn ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-locate2-light-gray text-sm">
            Don't have an account?{' '}
            <button
              onClick={onSwitchToSignUp}
              className="text-locate2-primary hover:text-locate2-dark-blue font-medium transition-colors"
            >
              Sign up
            </button>
          </p>
        </div>

        <div className="mt-4 text-center">
          <p className="text-locate2-light-gray text-xs">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
